import { CalendarDateTime } from "@internationalized/date";
import {
  CountryCode,
  getPreferredIdForStop,
  Leg,
  StopContext,
} from "@vygruppen/vy-train-map";
import { InfrastructureProviderEnum } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/InfrastructureProvider";
import { getInitialOpenEndedDuration } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/durationInputs/initialDurationValues";
import {
  createRulesFromPeriodicDuration,
  ruleToPeriod,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/durationInputs/periodicDurationUtils";
import {
  FormSchemaDuration,
  InfrastructureCommon,
  InfrastructureForm,
  OpenEndedFormDuration,
  PeriodicFormDuration,
  TimedFormDuration,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/formSchema";
import {
  InfrastructureRequest,
  OpenEndedRequestDuration,
  PeriodicRequestDuration,
  RequestDuration,
  RequestRedirectStretch,
  TimedRequestDuration,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/requestSchema";
import {
  Duration,
  InfrastructureResponse,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/responseSchema";
import {
  DescribedType,
  isCustomEmptyType,
  isCustomInputType,
} from "features/CenterContent/shared/operationalInformation/utils";
import { DefaultValues } from "react-hook-form";
import { dateTimeFormToRequest } from "shared/form/dateTimeSchema";
import { formatStretchName } from "shared/regex";
import { isPlannedInfraStructureEvent } from "shared/utils/infrastructureEvent";
import { Nullable } from "shared/utils/objectUtils";
import {
  getDefaultPrognosis,
  getPrognosisTime,
  validatePrognosis,
} from "features/CenterContent/shared/operationalInformation/prognosisUtils";

const getReason = (event: InfrastructureResponse) => {
  if (isPlannedInfraStructureEvent(event.type))
    return { type: "WORK_ON_TRACK" };
  return event.reason;
};

export const getCurrentCalendarDateTime = (plusHours?: number) => {
  const now = new Date();
  return new CalendarDateTime(
    now.getFullYear(),
    now.getMonth() + 1,
    now.getDate(),
    now.getHours() + (plusHours ?? 0),
    now.getMinutes(),
  );
};

const getInitialDuration = (duration: Duration): FormSchemaDuration => {
  switch (duration.type) {
    case "PERIODIC": {
      const rules = createRulesFromPeriodicDuration(duration);
      return { type: "PERIODIC", rules };
    }
    case "TIMED": {
      return {
        type: "TIMED",
        from_time: duration.from_time,
        to_time: duration.to_time,
      };
    }

    case "OPEN_ENDED": {
      return { ...duration, from_time: duration.from_time };
    }
  }
};

const getProviderFromCountryCode = (
  countryCode: CountryCode,
): InfrastructureProviderEnum => {
  switch (countryCode) {
    case "NO":
      return InfrastructureProviderEnum.BANE_NOR;
    case "SE":
      return InfrastructureProviderEnum.TRAFIKVERKET;
  }
};

const transformAffectedLegsToLegs = (
  affectedLegs: InfrastructureResponse["affectedLegs"],
  countryCode: CountryCode,
): Leg[] => {
  switch (countryCode) {
    case "NO":
      return affectedLegs.map<Leg>(({ fromStopId, toStopId }) => ({
        fromStop: {
          name: fromStopId.name ?? fromStopId.id,
          countryCode,
          stopIds: [fromStopId],
        },
        toStop: {
          name: toStopId.name ?? toStopId.id,
          countryCode,
          stopIds: [toStopId],
        },
      }));
    case "SE":
      return affectedLegs.map<Leg>(({ fromStopId, toStopId }) => ({
        fromStop: {
          name: fromStopId.name ?? fromStopId.id,
          countryCode,
          stopIds: [
            fromStopId,
            {
              id: `${countryCode}.${fromStopId.id}`,
              context: StopContext.JBV,
              name: fromStopId.name,
            },
          ],
        },
        toStop: {
          name: toStopId.name ?? toStopId.id,
          countryCode,
          stopIds: [
            toStopId,
            {
              id: `${countryCode}.${toStopId.id}`,
              context: StopContext.JBV,
              name: toStopId.name,
            },
          ],
        },
      }));
  }
};

const transformResponseRedirectStretches = (
  redirectStretches: InfrastructureResponse["redirectStretches"],
  countryCode: CountryCode,
): InfrastructureCommon["redirectStretches"] =>
  redirectStretches.map(({ name, legs }) => {
    switch (countryCode) {
      case "NO":
        return {
          name,
          legs: legs.map<Leg>((leg) => ({
            fromStop: {
              name: leg.fromStop,
              countryCode,
              stopIds: [{ id: leg.fromStop, context: StopContext.JBV }],
            },
            toStop: {
              name: leg.toStop,
              countryCode,
              stopIds: [{ id: leg.toStop, context: StopContext.JBV }],
            },
          })),
        };

      case "SE":
        return {
          name,
          legs: legs.map<Leg>((leg) => ({
            fromStop: {
              name: leg.fromStop,
              countryCode,
              stopIds: [
                { id: leg.fromStop, context: StopContext.TVS },
                {
                  id: `${countryCode}.${leg.fromStop}`,
                  context: StopContext.JBV,
                },
              ],
            },
            toStop: {
              name: leg.toStop,
              countryCode,
              stopIds: [
                { id: leg.toStop, context: StopContext.TVS },
                {
                  id: `${countryCode}.${leg.toStop}`,
                  context: StopContext.JBV,
                },
              ],
            },
          })),
        };
    }
  });

const transformLegsToAffectedLegs = (
  legs: Leg[],
): InfrastructureRequest["affectedLegs"] =>
  legs.map((leg) => ({
    fromStopId: getPreferredIdForStop(leg.fromStop)!,
    toStopId: getPreferredIdForStop(leg.toStop)!,
  }));

function defaultForm(
  countryCode: CountryCode,
): DefaultValues<InfrastructureForm> {
  return {
    infrastructureForm: {
      type: undefined,
      reason: { type: "" },
      actionCard: { type: "" },
      consequence: { type: "" },
      ...getDefaultPrognosis(),
      stretchName: "",
      affectedLegs: [],
      redirectStretches: [],
      durations: getInitialOpenEndedDuration(),
      isirkId: "",
      action: null,
      infrastructureProvider: getProviderFromCountryCode(countryCode),
    },
  };
}

export const responseToForm = (
  event: InfrastructureResponse | undefined,
  countryCode: CountryCode,
): DefaultValues<InfrastructureForm> => {
  if (!event) return defaultForm(countryCode);

  const form = {
    reason: getReason(event),
    action: event.action,
    actionCard: event.actionCard,
    consequence: event.consequence,
    prognosisType: event.prognosis,
    prognosisTime: getPrognosisTime(event.prognosis),
    stretchName: event.stretchName,
    affectedLegs: transformAffectedLegsToLegs(
      event.affectedLegs,
      event.countryCode,
    ),
    redirectStretches: transformResponseRedirectStretches(
      event.redirectStretches,
      event.countryCode,
    ),
    durations: getInitialDuration(event.durations),
    isirkId: event.isirkId,
    infrastructureProvider:
      event.infrastructureProvider === null
        ? InfrastructureProviderEnum.NONE
        : event.infrastructureProvider,
  };

  switch (event.type) {
    case "INFRASTRUCTURE_GENERAL_INFO":
    case "INFRASTRUCTURE_GENERAL_WARNING":
      return {
        infrastructureForm: {
          type: "INFRASTRUCTURE_GENERAL",
          ...form,
          durations: getInitialDuration(event.durations) as
            | TimedFormDuration
            | OpenEndedFormDuration,
          severity:
            event.type === "INFRASTRUCTURE_GENERAL_INFO" ? "INFO" : "WARNING",
          distributions: event.distributions!, // Must exist for custom infrastructure events
        },
      };

    case "INFRASTRUCTURE_PLANNED_MAINTENANCE":
      return {
        infrastructureForm: {
          type: event.type,
          ...form,
          durations: getInitialDuration(event.durations) as
            | TimedFormDuration
            | PeriodicFormDuration,
        },
      };

    case "INFRASTRUCTURE_STRETCH_CLOSED":
    case "INFRASTRUCTURE_STRETCH_PARTIALLY_CLOSED":
    case "INFRASTRUCTURE_SLOW_DRIVING":
      return {
        infrastructureForm: {
          type: event.type,
          ...form,
          durations: getInitialDuration(event.durations) as
            | TimedFormDuration
            | OpenEndedFormDuration,
        },
      };
  }
};

export const createRequestDurations = (
  durations: FormSchemaDuration,
): RequestDuration => {
  switch (durations.type) {
    case "TIMED":
      return {
        fromTime: dateTimeFormToRequest(durations.from_time),
        toTime: dateTimeFormToRequest(durations.to_time),
      };

    case "PERIODIC": {
      return { periods: durations.rules.flatMap((rule) => ruleToPeriod(rule)) };
    }

    case "OPEN_ENDED":
      return {
        fromTime: dateTimeFormToRequest(durations.from_time),
        timeHorizonInMinutes: Number(durations.extension_time_in_minutes),
      };
  }
};

export const validateTypeAndDescriptionNullable = (
  field: Nullable<DescribedType>,
) => {
  if (!field) return null;
  const { type, description } = field;
  const validatedType = type && type.length > 0;
  if (!validatedType) return null;
  const validatedDescription = isCustomInputType(type) && description;
  return validatedDescription ? { type, description } : { type };
};

/**
 * This can return null because of the emptyType check. If you need to
 * ensure that the field is not null, separate this function into two.
 */
export const validateTypeAndDescription = (field: DescribedType) => {
  const validated = validateTypeAndDescriptionNullable(field);
  if (!validated) {
    throw new Error(
      "This should not be possible. Ensure that formMethods.trigger() is ran before this.",
    );
  }
  const emptyType = isCustomEmptyType(validated.type);
  if (emptyType) return null;
  return validated;
};

const validateActionCard = (field: InfrastructureCommon["actionCard"]) => {
  const type = field?.type;
  if (!type || type === "") return null;
  return { type };
};

const transformRequestRedirectStretches = (
  redirectStretches: InfrastructureCommon["redirectStretches"],
): RequestRedirectStretch[] =>
  redirectStretches.map<RequestRedirectStretch>(({ name, legs }) => ({
    name,
    legs: legs.map(({ fromStop, toStop }) => ({
      fromStop: getPreferredIdForStop(fromStop)?.id ?? fromStop.stopIds[0].id,
      toStop: getPreferredIdForStop(toStop)?.id ?? fromStop.stopIds[0].id,
    })),
  }));

export const formToRequest = (
  formState: InfrastructureForm["infrastructureForm"],
  countryCode: CountryCode,
): InfrastructureRequest => {
  const form = {
    reason: validateTypeAndDescriptionNullable(formState.reason),
    action: validateTypeAndDescriptionNullable(formState.action),
    consequence: validateTypeAndDescriptionNullable(formState.consequence),
    stretchName: formatStretchName(formState.stretchName),
    affectedLegs: transformLegsToAffectedLegs(formState.affectedLegs),
    countryCode,
    infrastructureProvider:
      formState.infrastructureProvider === InfrastructureProviderEnum.NONE
        ? null
        : formState.infrastructureProvider,
  };

  switch (formState.type) {
    case "INFRASTRUCTURE_GENERAL":
      return {
        type: `INFRASTRUCTURE_GENERAL_${formState.severity}`,
        ...form,
        prognosis: validatePrognosis(
          formState.prognosisType,
          formState.prognosisTime,
        ),
        duration: createRequestDurations(
          formState.durations,
        ) as OpenEndedRequestDuration,
        distributions: formState.distributions,
      };

    case "INFRASTRUCTURE_PLANNED_MAINTENANCE":
      return {
        type: formState.type,
        ...form,
        isirkId: formState.isirkId === "" ? null : formState.isirkId,
        duration: createRequestDurations(formState.durations) as
          | TimedRequestDuration
          | PeriodicRequestDuration,
      };

    case "INFRASTRUCTURE_STRETCH_PARTIALLY_CLOSED":
    case "INFRASTRUCTURE_STRETCH_CLOSED":
    case "INFRASTRUCTURE_SLOW_DRIVING":
      return {
        type: formState.type,
        ...form,
        actionCard: validateActionCard(formState.actionCard),
        prognosis: validatePrognosis(
          formState.prognosisType,
          formState.prognosisTime,
        ),
        redirectStretches: transformRequestRedirectStretches(
          formState.redirectStretches,
        ),
        duration: createRequestDurations(
          formState.durations,
        ) as OpenEndedRequestDuration,
      };
  }
};
